import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type Props = {
  /** 折りたたみヘッダーに表示するタイトル */
  title: string;
  /** 初期状態で開いているか（デフォルト: tip=false, section=true） */
  defaultOpen?: boolean;
  /**
   * "tip" — 短い補足テキスト（childrenをTypographyでラップ）
   * "section" — テーブル等を含む大きめ補足（childrenをStackでラップ、左に黄色アクセントライン）
   */
  variant?: 'tip' | 'section';
  children: React.ReactNode;
};

/**
 * ワンポイント解説ボックス（開閉可能）
 *
 * variant="tip"（デフォルト）: 短い補足テキスト向け
 * variant="section": テーブルや複数段落を含む大きめの補足コンテンツ向け（左に黄色アクセントライン）
 */
const WorkflowTipBox: React.FC<Props> = ({ title, defaultOpen, variant = 'tip', children }) => {
  const isSection = variant === 'section';
  const [open, setOpen] = useState(defaultOpen ?? (isSection ? true : false));

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        ...(isSection && { borderLeft: 3, borderLeftColor: '#f9a825' }),
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          textAlign: 'left',
          bgcolor: 'rgba(249,168,37,0.06)',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'rgba(249,168,37,0.1)' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TipsAndUpdatesIcon sx={{ fontSize: 18, color: '#f9a825' }} />
          <Typography variant="body2" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: 'text.secondary',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </ButtonBase>
      <Collapse in={open}>
        {isSection ? (
          <Stack spacing={1.5} sx={{ px: 2, pb: 2, pt: 1.5 }}>
            {children}
          </Stack>
        ) : (
          <Box sx={{ px: 2, pb: 2, pt: 1.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {children}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default WorkflowTipBox;
